'use client';

import { useLocale, useTranslations } from 'next-intl';
import { ComponentPropsWithoutRef, ReactNode } from 'react';
import { VenueStatus, VenueStatusProps } from '@/entities/venue/ui/venue-status';
import { cn } from '@/shared/lib/cn';
import { formatAddress } from '@/shared/lib/formatAddress';
import { Address } from '@/shared/model/Address';
import { Card } from '@/shared/ui/card';
import { Icon } from '@/shared/ui/icon';
import { Media } from '@/shared/ui/media';
import { Tags } from '@/shared/ui/tags';
import { TagsT } from '@/shared/ui/tags/types';
import { VenueCardInfo } from './venue-card-info';
import './venue-card.scss';

type Props = ComponentPropsWithoutRef<typeof Card> & {
    imageUrl?: string;
    tags?: TagsT;
    isImageTagsVisible?: boolean;
    logoUrl?: string;
    title?: string;
    status?: Omit<VenueStatusProps, 'locale' | 't'>;
    address?: Address;
    telephone?: string;
    email?: string;
    actionComponent?: ReactNode;
};
export const VenueCard = ({
    id,
    imageUrl,
    tags,
    isImageTagsVisible,
    logoUrl,
    title,
    status,
    address,
    className,
    telephone,
    email,
    actionComponent,
    ...cardProps
}: Props) => {
    const t = useTranslations();
    const locale = useLocale();

    return (
        <Card
            className={cn('venue-card', className)}
            data-testid="venue-card"
            data-id={id}
            shadow
            {...cardProps}
        >
            {imageUrl && (
                <div className="venue-card__image">
                    {isImageTagsVisible && <Tags tags={tags} />}
                    <Media
                        data={{ url: imageUrl, alt: `venue-${title}-image` }}
                        fill
                        sizes="19.5rem"
                        data-testid="venue-card-image"
                    />
                </div>
            )}
            <div className="venue-card__content">
                <div className="venue-card__container">
                    {!isImageTagsVisible && <Tags tags={tags} appearance="outline" />}
                    {(title || logoUrl) && (
                        <div className="venue-card__title">
                            {logoUrl && (
                                <div className="venue-card__logo">
                                    <Media
                                        data={{ url: logoUrl, alt: `venue-${title}-logo` }}
                                        fill
                                        sizes="4rem"
                                        data-testid="venue-card-logo"
                                    />
                                </div>
                            )}
                            {title && <span data-testid="venue-card-title">{title}</span>}
                        </div>
                    )}
                    {status && <VenueStatus {...status} t={t} locale={locale} />}
                    {address && (
                        <VenueCardInfo
                            data-testid="venue-card-address"
                            icon={(svgProps) => <Icon name="scan" {...svgProps} />}
                        >
                            {formatAddress(address)}
                        </VenueCardInfo>
                    )}
                    {telephone && (
                        <VenueCardInfo
                            data-testid="venue-card-telephone"
                            icon={(svgProps) => <Icon name="phone" {...svgProps} />}
                            href={`tel:${telephone}`}
                        >
                            {telephone}
                        </VenueCardInfo>
                    )}
                    {email && (
                        <VenueCardInfo
                            data-testid="venue-card-email"
                            icon={(svgProps) => <Icon name="send" {...svgProps} />}
                            href={`mailto:${email}`}
                        >
                            {email}
                        </VenueCardInfo>
                    )}
                </div>
                {actionComponent}
            </div>
        </Card>
    );
};
